import type {Ref} from 'vue'

const MILLIMETERS_PER_INCH = 25.4
const formatValue = (v: any) => (typeof v === 'number' && Number.isFinite(v) ? v.toFixed(2) : String(v ?? '').trim())

export function computeInitialCcrsFilterValues(initialCcrsFilters: Ref<any[] | null>, showInchValues: Ref<boolean>, i18n: any) {
  return () => {
    const filters = initialCcrsFilters.value ?? []
    const perFilter: Record<string, {displays: string[]; rawValues: string[]; unit: string}> = {}
    const convertMillimetersToInches = (value: string) => {
      const num = Number.parseFloat(value.replaceAll(',', '.'))
      return Number.isFinite(num) ? (num / MILLIMETERS_PER_INCH).toFixed(3) : value
    }

    const attachUnit = (value: string, unit: string) => {
      if (!unit) {
        return value
      }
      const lowerUnit = unit.toLowerCase()
      const useInch = showInchValues.value && lowerUnit === 'mm'
      const displayUnit = i18n.tv(useInch ? 'inch' : lowerUnit, 'unitsOfMeasure')
      const displayValue = useInch ? convertMillimetersToInches(value) : value

      return `${displayValue} ${displayUnit}`
    }

    const addValue = (key: string, value: string, unit: string, customTranslatePrefix?: string) => {
      if (!value || value === '-') {
        return
      }

      if (!perFilter[key]) {
        perFilter[key] = {displays: [], rawValues: [], unit}
      }
      if (customTranslatePrefix) {
        value = i18n.tv(value, customTranslatePrefix)
      }
      perFilter[key].displays.push(attachUnit(value, unit))
      perFilter[key].rawValues.push(value)
    }

    const extractValue = (r: any): string => {
      if (Array.isArray(r)) {
        return ''
      }
      return formatValue(typeof r === 'object' ? (r.from ?? r.value) : r)
    }

    filters.forEach((f: any) => {
      const unit = (f.unitOfMeasure ?? f.unit ?? '').trim()
      const r = f.values ?? f.Value ?? f.value
      if (r === null || r === undefined) {
        return
      }

      const key = (f.shortName ?? f.technicalName ?? f.name ?? '').toString().toLowerCase()
      if (Array.isArray(r)) {
        r.forEach((it: any) => addValue(key, formatValue(it?.value ?? it?.label ?? it), unit, f.customTranslatePrefix))
      } else {
        addValue(key, extractValue(r), unit)
      }
    })

    const finalValues: string[] = []

    const consumed = new Set<string>()

    filters.forEach((f: any) => {
      const key = (f.shortName ?? f.technicalName ?? f.name ?? '').toString().toLowerCase()
      if (!key || !perFilter[key] || consumed.has(key)) {
        return
      }

      if ((key === 'outer_diameter' || key === 'inner_diameter') && perFilter.outer_diameter && perFilter.inner_diameter && !consumed.has('outer_diameter') && !consumed.has('inner_diameter')) {
        const [outer, inner] = [perFilter.outer_diameter, perFilter.inner_diameter]
        const unit = (outer.unit || inner.unit).toLowerCase()
        const [outerVal, innerVal] = [outer.rawValues[0], inner.rawValues[0]]

        if (showInchValues.value && unit === 'mm') {
          const unitLabel = i18n.tv('inch', 'unitsOfMeasure')
          finalValues.push(`${convertMillimetersToInches(outerVal)} ${unitLabel} x ${convertMillimetersToInches(innerVal)} ${unitLabel}`)
        } else {
          const unitLabel = i18n.tv(unit, 'unitsOfMeasure')
          finalValues.push(`${outerVal}${unitLabel} x ${innerVal}${unitLabel}`)
        }

        consumed.add('outer_diameter')
        consumed.add('inner_diameter')
      } else {
        finalValues.push(...perFilter[key].displays)
        consumed.add(key)
      }
    })

    Object.keys(perFilter).forEach(k => {
      if (!consumed.has(k)) {
        finalValues.push(...perFilter[k].displays)
        consumed.add(k)
      }
    })

    return finalValues.join(', ')
  }
}
